import { installedShellHost } from "../../host/installedHost.ts";
import { resolveSafeViewerUrl } from "./safeViewerUrl.ts";
import { desktopAppStatus, handOffToDesktop } from "./desktopHandoff.ts";
import {
  boundUrlKey,
  boundUrlSource,
  commitUrlOpen,
  isTrustedGesture,
  labelsFromCode,
  planUrlOpen,
  signedBoundUrl,
  urlOpenAllowed,
  type UrlOpenPlan,
  type UrlOpenRequest,
  type UserGesture,
} from "./boundUrlSet.ts";
import {
  cachedLinkCheck,
  checkLinkBeforeOpen,
  type LinkCheckTarget,
  type LinkGateResult,
} from "./linkGate.ts";

/**
 * Every web page Smart Manager opens runs inside its own browser, the
 * WebViewer window (owner rule, 2026-10-21). This is the one way the app
 * opens a link: buttons, `InAppLink`, notification actions, and the desktop
 * app's routed popups all come here. On the desktop the WebViewer is a
 * sandboxed webview in its own storage partition; in a web browser it is a
 * credentialless sandboxed iframe, and a site that refuses to be framed gets
 * the viewer's own explained state instead of a blank frame.
 *
 * Every open obeys the bound URL rule (boundUrlSet.ts, injection rule Q3):
 * only a bound URL opens, and only on the user's own gesture (pass its
 * event as `gesture`) or this turn's unused auto-open grant. A bound URL
 * opened without either shows held, waiting for a tap; an unbound one
 * opens nothing.
 *
 * The viewer checks every page with the platform link check (linkGate.ts)
 * before it loads it; a page that opens outside the app is checked here
 * first (openOutsideAfterCheck). A stopped address never opens anywhere.
 *
 * Guarded by tests/ci/inAppBrowserLinks.test.ts: a raw `target="_blank"`,
 * `window.open` or `shell.openExternal` outside the allowlist fails CI.
 */

export interface InAppBrowserOptions {
  /** The window title; the page's address shows regardless. */
  title?: string;
  /** The window that opened the page: the viewer is placed and closed with it. */
  parentId?: string;
  /** Sentences the answer cited, highlighted once the page loads (desktop). */
  highlightSentences?: readonly string[];
  /**
   * False when the source is known to refuse framing. A web browser then
   * shows the viewer's explained state at once instead of trying the frame.
   */
  canEmbed?: boolean;
  /**
   * In a web browser, a page that refuses framing goes straight to the
   * desktop app's own browser, even before this browser has seen the app
   * open (a flow whose next step is on that site, like posting a reply).
   * Without it, only a known install tries the desktop app first. Either
   * way the viewer opens too, with retry, download and a new tab, because a
   * web page can only guess whether the app opened (desktopHandoff.ts).
   */
  preferDesktop?: boolean;
  /**
   * Desktop: a website sent this page through the `smartmanager://open` link,
   * not the app. The viewer says so while it holds the page (any page can
   * fire that link, so it must never pass for the app's own).
   */
  sentFromWebsite?: boolean;
  /**
   * The user's own input event on the control that opens this page (the
   * click, tap or key press). Without one, the page opens only on this
   * turn's auto-open grant, else it shows held.
   */
  gesture?: UserGesture | null;
  /**
   * Desktop: the user clicked a link inside a page a WebViewer shows (the
   * desktop consumed that click's activation). The page could go there by
   * itself, so the link opens even when unbound.
   */
  browsing?: boolean;
}

const WEB_VIEWER = "WebViewer";

/**
 * Pages the in-app browser cannot serve: a call needs the camera and
 * microphone and a streaming player needs DRM, which the browsing partition
 * never grants. They open in the system browser, here and in the desktop
 * router. Each host matches itself and its subdomains. Kept equal to
 * SYSTEM_BROWSER_HOSTS in electron/inAppBrowser.js (tests/browser/inAppBrowser.test.ts).
 */
export const SYSTEM_BROWSER_HOSTS: readonly string[] = [
  // Calls
  "zoom.us",
  "meet.google.com",
  "teams.microsoft.com",
  "teams.live.com",
  "webex.com",
  "gotomeeting.com",
  "meet.goto.com",
  "meet.jit.si",
  "whereby.com",
  // DRM streaming
  "open.spotify.com",
  "netflix.com",
  "primevideo.com",
  "disneyplus.com",
  "max.com",
  "hbomax.com",
  "hulu.com",
  "paramountplus.com",
  "peacocktv.com",
  "tv.apple.com",
  "music.apple.com",
];

/**
 * Sites that refuse to be shown inside another web page (X-Frame-Options or
 * a CSP frame-ancestors rule), so a web browser's viewer explains that at
 * once instead of waiting out a blank frame. The desktop's webview is a
 * real browser and shows them all.
 */
export const FRAME_REFUSING_HOSTS: readonly string[] = [
  "reddit.com",
  "redd.it",
  "x.com",
  "twitter.com",
  "facebook.com",
  "instagram.com",
  "threads.net",
  "linkedin.com",
  "tiktok.com",
  "github.com",
  "google.com",
  "youtube.com",
  "amazon.com",
  "paypal.com",
  "apple.com",
  "microsoft.com",
  "live.com",
  "stackoverflow.com",
  "medium.com",
  "nytimes.com",
  "wsj.com",
  "bloomberg.com",
  "yelp.com",
  "airbnb.com",
  "booking.com",
  "ebay.com",
  "stripe.com",
];

function hostIn(url: string, hosts: readonly string[]): boolean {
  let hostname: string;
  try {
    hostname = new URL(url).hostname.toLowerCase().replace(/\.$/, "");
  } catch {
    return false;
  }
  return hosts.some((host) => hostname === host || hostname.endsWith(`.${host}`));
}

/** True for a call or a DRM player: it opens in the system browser, never in-app. */
export function opensOutsideApp(url: string): boolean {
  return hostIn(url, SYSTEM_BROWSER_HOSTS);
}

/** True when the site is known to refuse being framed by another web page. */
export function refusesFraming(url: string): boolean {
  return hostIn(url, FRAME_REFUSING_HOSTS);
}

/** Desktop webview contents id to the WebViewer window that holds it, so its popups open beside it. */
const viewerByContentsId = new Map<number, string>();

/** Records which WebViewer window holds a desktop webview; returns the unregister. */
export function registerWebViewerContents(contentsId: number, componentId: string): () => void {
  viewerByContentsId.set(contentsId, componentId);
  return () => {
    if (viewerByContentsId.get(contentsId) === componentId) viewerByContentsId.delete(contentsId);
  };
}

/** The WebViewer window holding a desktop webview, if one registered it. */
export function webViewerForContents(contentsId: number | undefined): string | undefined {
  return typeof contentsId === "number" ? viewerByContentsId.get(contentsId) : undefined;
}

function currentOrigin(): string | undefined {
  return typeof window !== "undefined" ? window.location.origin : undefined;
}

/**
 * The address the in-app browser would open, or null. Only http and https
 * without sign-in details pass; a relative path resolves against this
 * page, so a first-party page (`/terms`) opens too.
 */
export function inAppBrowserUrl(url: unknown): string | null {
  return typeof url === "string" ? resolveSafeViewerUrl(url, currentOrigin()) : null;
}

/**
 * Workspaces showing windows now. Only the signed-in workspace grid
 * registers, and only while it can show a WebViewer (not behind the billing
 * gate), so the landing, the Connections page, the legal gate and every
 * public page keep their links as plain new-tab links.
 */
let surfaces = 0;
const availabilityListeners = new Set<(available: boolean) => void>();

function announceAvailability(): void {
  const available = surfaces > 0;
  for (const listener of availabilityListeners) listener(available);
}

/** The workspace grid says it is showing windows a viewer can join; returns the release. */
export function registerInAppBrowserSurface(): () => void {
  surfaces += 1;
  if (surfaces === 1) announceAvailability();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    surfaces -= 1;
    if (surfaces === 0) announceAvailability();
  };
}

/** Calls `listener` whenever in-app browsing turns on or off; returns the unsubscribe. */
export function onInAppBrowserAvailability(listener: (available: boolean) => void): () => void {
  availabilityListeners.add(listener);
  return () => availabilityListeners.delete(listener);
}

/** True when this page can show web pages inside the app: a workspace grid is up to hold the viewer. */
export function canBrowseInApp(): boolean {
  return surfaces > 0 && installedShellHost() !== null;
}

/**
 * Viewers asked for but not yet in the workspace: opening a window first
 * loads its code, so for a moment the workspace can't show a second click
 * on the same page, or a second page from the same parent, as taken.
 */
const pendingUrls = new Set<string>();
const pendingParents = new Set<string>();

/**
 * Opens `url` in the in-app browser: focuses the WebViewer already showing
 * it, else opens one. The viewer loads the page when this open is allowed
 * (a gesture, a grant, a code constant, or browsing; see the module note)
 * and otherwise holds it for a tap. A page a website sent
 * (`sentFromWebsite`) always waits, and its viewer opens even for an
 * unbound address, shown as text. Returns false, opening nothing, when the
 * address is otherwise unbound or unsafe, is a call or DRM player (opensOutsideApp), or this page has no
 * workspace for a viewer (a public page). In a web browser, an allowed
 * site that refuses framing may also go to the desktop app's own browser
 * (see `preferDesktop` and desktopHandoff.ts).
 */
export function openInAppBrowser(url: unknown, options: InAppBrowserOptions = {}): boolean {
  const key = inAppViewerKey(url);
  const host = installedShellHost();
  if (!key || !host) return false;
  // Any website can fire the desktop's open link, so that page never loads
  // by itself; the viewer still opens, saying where it came from, and shows
  // an unbound address as text the user can retype.
  if (options.sentFromWebsite) return openViewer(key, options);
  const request = { gesture: options.gesture, browsing: options.browsing, forViewer: true };
  const plan = planUrlOpen(key, request);
  if (plan.decision === "unbound") return false;
  // The open is recorded (its grant spent, a ticket left for the viewer)
  // only once the viewer is on its way.
  if (!openViewer(key, options)) return false;
  const allowed = commitUrlOpen(plan, request);
  // A web browser's viewer can only explain a site that refuses framing, so
  // that site goes to the desktop app's browser when the user wants that or
  // the app is likely here. The viewer opens as well and shows how the
  // handoff went, with its fallbacks: losing focus does not prove the app
  // opened, so a wrong guess must never leave the user with nothing.
  if (
    allowed
    && !host.desktop()
    && (options.canEmbed === false || refusesFraming(key))
    && (options.preferDesktop || desktopAppStatus() === "installed")
  ) handOffToDesktop(key);
  return true;
}

/**
 * The address a WebViewer would show for `url`, or null when no viewer can:
 * an unsafe address, a call or DRM player (opensOutsideApp), or a page with
 * no workspace to hold a viewer.
 */
function inAppViewerKey(url: unknown): string | null {
  const safeUrl = inAppBrowserUrl(url);
  if (!safeUrl || !installedShellHost() || !canBrowseInApp() || opensOutsideApp(safeUrl)) return null;
  return boundUrlKey(safeUrl);
}

function openViewer(safeUrl: string, options: InAppBrowserOptions): boolean {
  const host = installedShellHost();
  if (!host) return false;
  // Still loading from a click a moment ago: that viewer is on its way.
  if (pendingUrls.has(safeUrl)) return true;

  const workspace = host.workspace.getState();
  const open = workspace.getComponents().find((component) => (
    component.name === WEB_VIEWER && component.props?.url === safeUrl
  ));
  if (open) {
    if (open.isMinimized) workspace.restoreComponent(open.id);
    workspace.bringComponentToFront(open.id);
    return true;
  }

  // The workspace keeps one window of a kind per parent (it re-creates
  // children on restore), so a second page from the same parent would be
  // dropped. That page opens unattached instead.
  const parentId = options.parentId
    && !pendingParents.has(options.parentId)
    && !workspace.getComponents().some((component) => (
      component.name === WEB_VIEWER && component.parentComponentId === options.parentId
    )) ? options.parentId : undefined;
  // A search-result or outside-text page is named by its host, never by the
  // text a model or sender put beside the link (labelsFromCode).
  const title = labelsFromCode(boundUrlSource(safeUrl)) ? undefined : options.title?.trim() || undefined;
  // The server's binding rides along, so a restored window verifies it again.
  const signed = signedBoundUrl(safeUrl);
  const width = typeof window !== "undefined" ? Math.round(window.innerWidth * 0.45) : 640;
  const height = typeof window !== "undefined" ? Math.round(window.innerHeight * 0.55) : 560;
  pendingUrls.add(safeUrl);
  if (parentId) pendingParents.add(parentId);
  host.openComponent({
    name: WEB_VIEWER,
    props: {
      url: safeUrl,
      ...(title ? { title } : {}),
      size: { width, height },
      ...(options.highlightSentences?.length ? { highlightSentences: [...options.highlightSentences] } : {}),
      ...(options.canEmbed === false ? { canEmbed: false } : {}),
      ...(options.sentFromWebsite ? { sentFromWebsite: true } : {}),
      ...(signed ? { boundUrls: [signed] } : {}),
      score: 6,
      reason: `Opening ${title ?? new URL(safeUrl).hostname}`,
    },
    ...(parentId ? { parentId } : {}),
  }).catch((error: unknown) => {
    console.error("The page could not open in Smart Manager.", error);
  }).finally(() => {
    pendingUrls.delete(safeUrl);
    if (parentId) pendingParents.delete(parentId);
  });
  return true;
}

/**
 * Opens a web page from a link, a button, a gesture or a voice command: in
 * the in-app browser when it can (openInAppBrowser), else in a new tab of
 * the system browser, for a call or DRM player or a page with no workspace
 * for a viewer. That new tab opens only when the open is allowed now (the
 * user's gesture or this turn's grant): there is no viewer to hold it in.
 * On the desktop the new tab reaches electron/inAppBrowser.js, which sends
 * it to the system browser. Returns false when nothing opened.
 */
export function openWebPage(url: unknown, options: InAppBrowserOptions = {}): boolean {
  // One decision per open: a page a viewer can show goes only to the
  // viewer, and anything else only to the new tab.
  if (inAppViewerKey(url)) return openInAppBrowser(url, options);
  const target = boundUrlKey(inAppBrowserUrl(url));
  if (!target || typeof window === "undefined" || options.sentFromWebsite) return false;
  const request = { gesture: options.gesture };
  const plan = planUrlOpen(target, request);
  if (!urlOpenAllowed(plan.decision)) return false;
  return openOutsideAfterCheck(plan, request, "page");
}

/**
 * Why a page opens outside Smart Manager. Only these: a call needs the camera
 * and microphone, a streaming player needs DRM, a file download belongs to the
 * operating system, and a mail or phone link to its system app.
 */
export type OutsideAppReason = "call" | "drm" | "download" | "mail";

const OUTSIDE_SCHEMES = new Set(["mailto:", "tel:"]);

// One plain address: no display name, no second recipient, no subject or body.
const MAIL_ADDRESS = /^[^\s@,;:<>()[\]"]+@[^\s@,;:<>()[\]"]+$/;

/**
 * A mail link reduced to its one address, or a phone link to its number:
 * the system app opens with nothing else filled in, so a link cannot write
 * the user's data into a draft or dial extra digits.
 */
function plainOutsideLink(parsed: URL): string | null {
  if (parsed.protocol === "mailto:") {
    let address: string;
    try {
      address = decodeURIComponent(parsed.pathname).trim();
    } catch {
      return null;
    }
    return MAIL_ADDRESS.test(address) ? `mailto:${address}` : null;
  }
  const number = parsed.pathname.replace(/[\s().-]/g, "");
  return /^\+?\d{3,20}$/.test(number) ? `tel:${number}` : null;
}

/**
 * The address openOutsideApp would open, or null: an in-app-safe web
 * address, a mailto link to one address, or a tel link to one number.
 */
export function outsideAppUrl(url: unknown): string | null {
  if (typeof url !== "string") return null;
  try {
    const parsed = new URL(url);
    if (OUTSIDE_SCHEMES.has(parsed.protocol)) return plainOutsideLink(parsed);
  } catch {
    // Not absolute: a relative path resolves below.
  }
  return inAppBrowserUrl(url);
}

/**
 * The desktop app's opener, or null in a web browser, where a new tab or the
 * page's own mail handler already is outside the app.
 */
function desktopOpener(): ((url: string) => Promise<boolean>) | null {
  return installedShellHost()?.desktop()?.openOutsideApp ?? null;
}

/** True when openOutsideApp goes through the desktop app (it cannot return a window to steer). */
export function opensOutsideThroughDesktop(): boolean {
  return desktopOpener() !== null;
}

/**
 * Opens what the in-app browser cannot serve outside Smart Manager: on the
 * desktop the operating system's handler (it needs the click that called
 * this), in a web browser a new tab or the mail or phone app. Every stated
 * exception to the in-app browser calls this or renders OutsideAppLink, so
 * the reason in the code is what the user gets. A mail or phone link opens
 * on the user's own gesture; a web address follows the bound URL rule, with
 * no held state to fall back on. Returns false when nothing opened.
 */
export function openOutsideApp(url: unknown, reason: OutsideAppReason, gesture?: UserGesture | null): boolean {
  const safeUrl = outsideAppUrl(url);
  if (!safeUrl || typeof window === "undefined") return false;
  const systemApp = OUTSIDE_SCHEMES.has(new URL(safeUrl).protocol);
  // A web address opens as the exact address the rule allowed (the bound
  // key, the signed binding's own bytes), never the caller's spelling of it.
  // Its open is recorded (a grant spent) only as it is handed off.
  if (!systemApp) {
    const request = { gesture };
    const plan = planUrlOpen(safeUrl, request);
    if (!urlOpenAllowed(plan.decision)) return false;
    return openOutsideAfterCheck(plan, request, reason);
  }
  if (!isTrustedGesture(gesture)) return false;
  const desktop = desktopOpener();
  if (desktop) {
    handToDesktop(desktop, safeUrl, reason);
    return true;
  }
  // in-app-browser exception: a mail or phone link hands off to the system app and leaves this page in place.
  window.location.assign(safeUrl);
  return true;
}

function handToDesktop(open: (url: string) => Promise<boolean>, url: string, reason: OutsideAppReason | "page"): void {
  void open(url).then((opened) => {
    if (!opened) console.warn(`The desktop app did not open the ${reason} page outside Smart Manager.`);
  }, (error: unknown) => {
    console.error(`The ${reason} page could not open outside Smart Manager.`, error);
  });
}

/**
 * The desktop's system opener, armed for one outside open taken in this
 * click (the desktop refuses an outside web page it was not armed for).
 * A desktop build from before the check cannot be armed; the handoff is then
 * tried as before (listed call hosts still open, others may be denied).
 */
function armedDesktopOpener(): ((url: string) => Promise<boolean>) | null {
  const desktop = desktopOpener();
  if (desktop) installedShellHost()?.desktop()?.armOutsideAppOpen?.();
  return desktop;
}

/** What the platform link check asks about for an address that opens outside. */
function outsideCheckTarget(key: string): LinkCheckTarget {
  const binding = signedBoundUrl(key);
  return binding ? { kind: "bound", binding } : { kind: "unbound", url: key, typed: false };
}

/**
 * The address to open outside after the check, or null. A stopped address
 * opens nowhere; its viewer shows why, when this page has a workspace. A
 * short link's destination opens only on the user's own tap. A page the
 * check would open hardened still opens: an outside browser cannot be
 * hardened, and a call or a download has nowhere else to go.
 */
function checkedOutsideUrl(key: string, result: LinkGateResult, byUser: boolean): string | null {
  if (result.policy === "blocked" || !result.loadUrl) {
    if (installedShellHost() && canBrowseInApp()) openViewer(key, {});
    return null;
  }
  if (result.redirected && !byUser) return null;
  return result.loadUrl;
}

/**
 * Where an outside open goes once its check answers, taken in the user's
 * click so it survives the wait: on the desktop an armed handoff, in a web
 * browser an empty tab with no way back to this page. Null when there is none.
 */
function reserveOutside(reason: OutsideAppReason | "page"): { go: (url: string) => void; drop: () => void } | null {
  const desktop = armedDesktopOpener();
  if (desktop) return { go: (url) => handToDesktop(desktop, url, reason), drop: () => {} };
  // in-app-browser exception: an empty tab taken on the click; it goes to the page only after the check passes.
  const tab = window.open("about:blank", "_blank");
  if (!tab) return null;
  try {
    tab.opener = null;
    const noReferrer = tab.document.createElement("meta");
    noReferrer.name = "referrer";
    noReferrer.content = "no-referrer";
    tab.document.head?.append(noReferrer);
  } catch {
    // A tab that cannot be prepared is not used.
    tab.close();
    return null;
  }
  return {
    go: (url) => {
      try {
        // in-app-browser exception: the tab taken on the click goes to the checked page.
        tab.location.replace(url);
      } catch {
        tab.close();
      }
    },
    drop: () => tab.close(),
  };
}

/**
 * Opens an allowed outside open (`plan`) after the platform link check, the
 * same check the in-app browser runs. An answer known this session opens at
 * once in the click; otherwise the tab or handoff is taken now and used when
 * the check passes. The open is recorded (a grant spent) as it is taken.
 */
function openOutsideAfterCheck(plan: UrlOpenPlan, request: UrlOpenRequest, reason: OutsideAppReason | "page"): boolean {
  const key = plan.key;
  if (!key) return false;
  const target = outsideCheckTarget(key);
  const byUser = plan.decision === "gesture";
  const known = cachedLinkCheck(target);
  if (known) {
    const url = checkedOutsideUrl(key, known, byUser);
    if (!url || !commitUrlOpen(plan, request)) return false;
    const desktop = armedDesktopOpener();
    if (desktop) {
      handToDesktop(desktop, url, reason);
    } else {
      // in-app-browser exception: the stated reason (a call, DRM, a download, no viewer) is why this page cannot open in-app; checked.
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return true;
  }
  const out = reserveOutside(reason);
  if (!out) return false;
  if (!commitUrlOpen(plan, request)) {
    out.drop();
    return false;
  }
  void checkLinkBeforeOpen(target).then((result) => {
    const url = checkedOutsideUrl(key, result, byUser);
    if (url) out.go(url);
    else out.drop();
  });
  return true;
}
